import { useEffect, useRef } from "react";

interface Star {
  x: number;
  y: number;
  z: number;
  prevX: number;
  prevY: number;
  speed: number;
  color: string;
  twinklePhase: number;
  twinkleSpeed: number;
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

const STAR_COUNT = 220;
const COLORS = [
  "255,255,255",
  "200,220,255",
  "180,200,255",
  "255,240,220",
  "255,220,200",
  "220,240,255",
];

function createStar(cx: number, cy: number): Star {
  const angle = Math.random() * Math.PI * 2;
  const z = Math.random() * 0.98 + 0.01;
  return {
    x: cx,
    y: cy,
    z,
    prevX: cx,
    prevY: cy,
    speed: Math.random() * 0.0015 + 0.0005,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    twinklePhase: Math.random() * Math.PI * 2,
    twinkleSpeed: Math.random() * 2 + 1,
  };
}

function resetStar(star: Star, cx: number, cy: number) {
  const angle = Math.random() * Math.PI * 2;
  const offset = Math.random() * 30;
  star.x = cx + Math.cos(angle) * offset;
  star.y = cy + Math.sin(angle) * offset;
  star.z = 0.01 + Math.random() * 0.05;
  star.prevX = star.x;
  star.prevY = star.y;
  star.speed = Math.random() * 0.0015 + 0.0005;
  star.color = COLORS[Math.floor(Math.random() * COLORS.length)];
  star.twinklePhase = Math.random() * Math.PI * 2;
  star.twinkleSpeed = Math.random() * 2 + 1;
}

const SpaceCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isVisibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Pause animation when not visible
    const observer = new IntersectionObserver(
      ([entry]) => { isVisibleRef.current = entry.isIntersecting; },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const ctx = canvas.getContext("2d");
    if (!ctx) { observer.disconnect(); return; }

    let animId: number;
    let width = 0;
    let height = 0;
    let cx = 0;
    let cy = 0;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      width = rect?.width || window.innerWidth;
      height = rect?.height || window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = width / 2;
      cy = height / 2;
    };

    resize();
    window.addEventListener("resize", resize);

    // Initialize stars at random depths
    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      const s = createStar(cx, cy);
      // Distribute across depths so screen starts full
      const angle = Math.random() * Math.PI * 2;
      s.z = Math.random() * 0.95 + 0.01;
      const spread = (s.z * Math.max(width, height)) / 1.5;
      s.x = cx + Math.cos(angle) * spread * (Math.random() * 0.8 + 0.2);
      s.y = cy + Math.sin(angle) * spread * (Math.random() * 0.8 + 0.2);
      s.prevX = s.x;
      s.prevY = s.y;
      stars.push(s);
    }

    // Shooting stars
    const shootingStars: ShootingStar[] = [];
    let lastShootingTime = 0;

    let time = 0;

    const draw = (timestamp: number) => {
      if (!isVisibleRef.current || document.documentElement.classList.contains("chat-open")) {
        animId = requestAnimationFrame(draw);
        return;
      }
      const dt = 16; // fixed step
      time += dt * 0.001;

      ctx.clearRect(0, 0, width, height);

      // Update and draw stars
      for (const star of stars) {
        star.prevX = star.x;
        star.prevY = star.y;

        // Move outward from center
        const dx = star.x - cx;
        const dy = star.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        star.z += star.speed * dt * 0.03;
        const expansion = 1 + star.z * star.z * 2;
        star.x = cx + (dx / dist) * dist * (1 + star.speed * dt * 0.08 * expansion);
        star.y = cy + (dy / dist) * dist * (1 + star.speed * dt * 0.08 * expansion);

        // Check bounds
        if (
          star.x < -50 || star.x > width + 50 ||
          star.y < -50 || star.y > height + 50 ||
          star.z > 1
        ) {
          resetStar(star, cx, cy);
          continue;
        }

        // Size grows with depth
        const size = 0.3 + star.z * 3.5;

        // Twinkle
        const twinkle = 0.6 + 0.4 * Math.sin(time * star.twinkleSpeed + star.twinklePhase);
        const alpha = Math.min(1, star.z * 2.5) * twinkle;

        // Draw trail/streak
        const trailLen = Math.min(dist * 0.03 * star.z, 15);
        if (trailLen > 1 && dist > 5) {
          const ndx = (star.x - cx) / dist;
          const ndy = (star.y - cy) / dist;
          const gradient = ctx.createLinearGradient(
            star.x - ndx * trailLen, star.y - ndy * trailLen,
            star.x, star.y
          );
          gradient.addColorStop(0, `rgba(${star.color},0)`);
          gradient.addColorStop(1, `rgba(${star.color},${alpha * 0.7})`);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = size * 0.6;
          ctx.beginPath();
          ctx.moveTo(star.x - ndx * trailLen, star.y - ndy * trailLen);
          ctx.lineTo(star.x, star.y);
          ctx.stroke();
        }

        // Draw star point
        ctx.beginPath();
        ctx.arc(star.x, star.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${star.color},${alpha})`;
        ctx.fill();

        // Glow for larger stars
        if (size > 2) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${star.color},${alpha * 0.15})`;
          ctx.fill();
        }
      }

      // Shooting stars
      if (timestamp - lastShootingTime > 8000 + Math.random() * 7000) {
        if (shootingStars.length < 3) {
          const edge = Math.random();
          let sx: number, sy: number, svx: number, svy: number;
          if (edge < 0.5) {
            sx = Math.random() * width;
            sy = -10;
            svx = (Math.random() - 0.5) * 8;
            svy = 4 + Math.random() * 6;
          } else {
            sx = -10;
            sy = Math.random() * height * 0.5;
            svx = 5 + Math.random() * 5;
            svy = 2 + Math.random() * 3;
          }
          shootingStars.push({
            x: sx, y: sy, vx: svx, vy: svy,
            life: 0, maxLife: 40 + Math.random() * 30,
            size: 1.5 + Math.random() * 1.5,
          });
          lastShootingTime = timestamp;
        }
      }

      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const ss = shootingStars[i];
        ss.x += ss.vx;
        ss.y += ss.vy;
        ss.life++;

        const progress = ss.life / ss.maxLife;
        const ssAlpha = progress < 0.1 ? progress * 10 : progress > 0.7 ? (1 - progress) / 0.3 : 1;

        // Trail
        const trailLength = 25;
        const gradient = ctx.createLinearGradient(
          ss.x - ss.vx * trailLength, ss.y - ss.vy * trailLength,
          ss.x, ss.y
        );
        gradient.addColorStop(0, `rgba(255,255,255,0)`);
        gradient.addColorStop(1, `rgba(255,255,255,${ssAlpha * 0.9})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = ss.size;
        ctx.beginPath();
        ctx.moveTo(ss.x - ss.vx * trailLength, ss.y - ss.vy * trailLength);
        ctx.lineTo(ss.x, ss.y);
        ctx.stroke();

        // Head glow
        ctx.beginPath();
        ctx.arc(ss.x, ss.y, ss.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${ssAlpha * 0.5})`;
        ctx.fill();

        if (ss.life >= ss.maxLife || ss.x > width + 50 || ss.y > height + 50) {
          shootingStars.splice(i, 1);
        }
      }

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0"
      style={{ willChange: "transform" }}
    />
  );
};

export default SpaceCanvas;
